import { useHistory } from 'react-router-dom';
import {
  Box, Button,
  FormControl, FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AsyncSelect from 'react-select/async';
import { Field, Form } from 'react-final-form';
import * as Yup from 'yup';
import { setIn } from 'final-form';
import {
  AgeGroupListService,
  GenderListService, GoalListService,
  RelationshipStatusListService,
} from '../../services/AuthService';
import { CreateQuestionnaire } from '../../services/QuestionnaireService';

function Select({
  isDisabled, name, placeholder, loadOptions, isMulti, value, onChange, onBlur, ...props
}) {
  return (
    <AsyncSelect
      isDisabled={isDisabled}
      name={name}
      placeholder={placeholder}
      loadOptions={loadOptions}
      isMulti={isMulti}
      cacheOptions
      defaultOptions
      onChange={(v) => {
        onChange(v);
      }}
      onBlur={onBlur}
      value={value}
      {...props}
    />
  );
}

const schema = Yup.object({
  question: Yup.string().required('Required'),
});
const validate = async (values) => {
  try {
    await schema.validate(values, { abortEarly: false });
  } catch (err) {
    return err.inner.reduce((formError, innerError) => setIn(formError, innerError.path, innerError.message), {});
  }
  return undefined;
};
export function NewQuestionPage() {
  const history = useHistory();

  const textColor = useColorModeValue('navy.700', 'white');

  const loadAgeGroupsListOptions = (inputValue) => AgeGroupListService(inputValue)
    .then((response) => response.data.results.map((item) => ({
      value: item.id,
      label: `${item.min_age} - ${item.max_age}`,
    })));

  const loadGenderListOptions = (inputValue) => GenderListService(inputValue)
    .then((response) => response.data.map((item) => ({
      value: item.id,
      label: item.label,
    })));

  const loadGoalListOptions = () => GoalListService()
    .then((response) => response.data.map((item) => ({
      value: item.id,
      label: item.label,
    })));
  const loadRelationShipListOptions = (inputValue) => RelationshipStatusListService(inputValue)
    .then((response) => response.data.map((item) => ({
      value: item.id,
      label: item.label,
    })));

  const queryClient = useQueryClient();

  return (
    <>
      <Box h={82} />
      <Box bgColor="white" p={8} borderRadius={8}>
        <Form
          validate={validate}
          onSubmit={(values) => CreateQuestionnaire({
            question: values.question,
            is_draft: values.is_draft,
            age_group: values.age_group?.map(({ value }) => value),
            goal: values.goal?.map(({ value }) => value),
            relationship_status: values.relationship_status?.map(({ value }) => value),
            gender: values.gender?.map(({ value }) => value),
          }).then((resp) => {
            if (resp.status < 300) {
              queryClient.invalidateQueries({ queryKey: ['questionnaire'] });
              queryClient.resetQueries({ queryKey: ['questionnaire'] });
              history.push('/admin/questionnaire');
            }
          })}
          render={({
            handleSubmit,
            valid,
            dirty,
            submitting,
            form,
          }) => (
            <form onSubmit={handleSubmit} style={{ flex: 1, overflow: 'auto' }}>
              <div
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <Field
                  name="question"
                  render={({ input, meta }) => (
                    <FormControl isInvalid={meta.touched && meta.invalid}>
                      <FormLabel
                        ms="4px"
                        fontSize="sm"
                        fontWeight="500"
                        color={textColor}
                        display="flex"
                        mt="24px"
                      >
                        Question
                      </FormLabel>
                      <InputGroup size="md" isRequired>
                        <Input
                          isRequired
                          fontSize="sm"
                          size="lg"
                          {...input}
                        />
                      </InputGroup>
                      {meta.touched && meta.invalid && <FormErrorMessage>{meta.error}</FormErrorMessage>}
                    </FormControl>
                  )}
                />

                <Field
                  name="gender"
                  render={({ input, meta }) => (
                    <FormControl isInvalid={meta.touched && meta.invalid}>
                      <FormLabel
                        ms="4px"
                        fontSize="sm"
                        fontWeight="500"
                        color={textColor}
                        display="flex"
                        mt="24px"
                      >
                        Gender
                      </FormLabel>
                      <InputGroup size="md">
                        <Select
                          isMulti
                          isDisabled={false}
                          loadOptions={loadGenderListOptions}
                          {...input}
                          styles={{
                            container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
                          }}
                        />
                      </InputGroup>
                      {meta.touched && meta.invalid && <FormErrorMessage>{meta.error}</FormErrorMessage>}
                    </FormControl>
                  )}
                />
                <Field
                  name="goal"
                  render={({ input, meta }) => (
                    <FormControl isInvalid={meta.touched && meta.invalid}>
                      <FormLabel
                        ms="4px"
                        fontSize="sm"
                        fontWeight="500"
                        color={textColor}
                        display="flex"
                        mt="24px"
                      >
                        Goal
                      </FormLabel>
                      <InputGroup size="md">
                        <Select
                          isMulti
                          isDisabled={false}
                          loadOptions={loadGoalListOptions}
                          {...input}
                          styles={{
                            container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
                          }}
                        />
                      </InputGroup>
                      {meta.touched && meta.invalid && <FormErrorMessage>{meta.error}</FormErrorMessage>}
                    </FormControl>
                  )}
                />
                <Field
                  name="age_group"
                  render={({ input, meta }) => (
                    <FormControl isInvalid={meta.touched && meta.invalid}>
                      <FormLabel
                        ms="4px"
                        fontSize="sm"
                        fontWeight="500"
                        color={textColor}
                        display="flex"
                        mt="24px"
                      >
                        Age Groups
                      </FormLabel>
                      <InputGroup size="md">
                        <Select
                          isMulti
                          isDisabled={false}
                          loadOptions={loadAgeGroupsListOptions}
                          {...input}
                          styles={{
                            container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
                          }}
                        />
                      </InputGroup>
                      {meta.touched && meta.invalid && <FormErrorMessage>{meta.error}</FormErrorMessage>}
                    </FormControl>
                  )}
                />

                <Field
                  name="relationship_status"
                  render={({ input, meta }) => (
                    <FormControl isInvalid={meta.touched && meta.invalid}>
                      <FormLabel
                        ms="4px"
                        fontSize="sm"
                        fontWeight="500"
                        color={textColor}
                        display="flex"
                        mt="24px"
                      >
                        Relationship Status
                      </FormLabel>
                      <InputGroup size="md">
                        <Select
                          isMulti
                          isDisabled={false}
                          loadOptions={loadRelationShipListOptions}
                          {...input}
                          styles={{
                            container: (baseStyles) => ({ ...baseStyles, width: '100%' }),
                          }}
                        />
                      </InputGroup>
                      {meta.touched && meta.invalid && <FormErrorMessage>{meta.error}</FormErrorMessage>}
                    </FormControl>
                  )}
                />

              </div>
              <Box mt="12px">
                <Button colorScheme="blue" me="4px" onClick={() => history.push('/admin/questionnaire')}>
                  Close
                </Button>
                <Button
                  colorScheme="green"
                  me="4px"
                  type="submit"
                  disabled={!dirty || !valid || submitting}
                  onClick={() => {
                    form.change('is_draft', false);
                  }}
                >
                  Publish
                </Button>
                <Button
                  colorScheme="green"
                  me="4px"
                  type="submit"
                  disabled={!dirty || !valid || submitting}
                  onClick={() => {
                    form.change('is_draft', true);
                  }}
                >
                  Save as Draft
                </Button>
              </Box>
            </form>
          )}
        />
      </Box>
    </>
  );
}
